import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type AutoApplication } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useToast } from "./Toast";
import { Badge, Button, Card, Empty } from "./ui";

/**
 * "Auto-applied (last 7 days)" — Tier 1 of the trust ladder, with one-click Undo (D-33).
 *
 * This panel is what makes auto-apply acceptable at all. A model that changes fields without asking is
 * only tolerable if every change is visible in one place and reversible with one click — otherwise the
 * operator's only way to know what happened is to read the timeline of every lead, which nobody does.
 *
 * Undo restores every prior value from the application's own `before_values` snapshot, taken at the
 * moment of the write: the values are not recomputed later, because by then the next delivery may have
 * moved the same field again. The revert is a compensating write plus an `agent_reverted` event — the
 * ledger is append-only, so "this did not stand" is a new fact, never an erasure.
 *
 * Every Undo also becomes a row in the feedback corpus, which is what drives the monthly demotion: a
 * class whose 7-day revert rate goes over 10% loses Tier 1 until a later month comes in under 5%.
 */
const CLASS_LABEL: Record<string, string> = {
  reply_class: "Reply class",
  tags: "Tags",
  score: "Score",
  next_action: "Next action",
};

function summarise(values: Record<string, unknown> | undefined): string {
  const parts = Object.entries(values ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${Array.isArray(v) ? v.join(", ") : String(v)}`);
  return parts.length ? parts.join(" · ") : "—";
}

function Row({
  application,
  showLead,
}: {
  application: AutoApplication;
  showLead: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const undo = useMutation({
    mutationFn: () => api.undoAutoApplied(application.id),
    onSuccess: () => {
      toast.push(`${CLASS_LABEL[application.change_class] ?? application.change_class} undone`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <li
      className="rounded border px-2 py-1.5 text-xs"
      data-testid={`auto-applied-${application.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="info">{CLASS_LABEL[application.change_class] ?? application.change_class}</Badge>
        {showLead && (
          <Link to={`/leads/${application.lead_id}`} className="font-medium hover:underline">
            {application.lead_name ?? "(unnamed)"}
          </Link>
        )}
        {application.confidence !== null && application.confidence !== undefined && (
          <span className="tabular-nums text-fg-muted">
            confidence {Number(application.confidence).toFixed(2)}
          </span>
        )}
        <span className="ml-auto tabular-nums text-fg-muted">
          {formatDateTime(application.applied_at)}
        </span>
      </div>
      <p className="mt-1 break-words text-[11px] text-fg-muted">
        {summarise(application.before_values)} → {summarise(application.after_values)}
      </p>
      <div className="mt-1.5 flex items-center gap-2">
        <Button
          size="sm"
          disabled={undo.isPending}
          onClick={() => undo.mutate()}
          data-testid={`undo-${application.id}`}
          title="Restore every value this replaced, and log the revert"
        >
          {undo.isPending ? "Undoing…" : "Undo"}
        </Button>
        <span className="text-[11px] text-fg-muted">
          undoable until {formatDateTime(application.undoable_until)}
        </span>
      </div>
    </li>
  );
}

export function AutoAppliedPanel({ leadId }: { leadId?: string }) {
  const { data } = useQuery({
    queryKey: ["auto-applied", leadId ?? "all"],
    queryFn: () => api.autoApplied(leadId),
  });

  if (!data) return null;
  if (leadId && data.length === 0) return null;

  return (
    <Card
      title={`Auto-applied (last 7 days)${data.length ? ` — ${data.length}` : ""}`}
      data-testid="auto-applied-panel"
    >
      {data.length === 0 ? (
        <Empty
          title="Nothing auto-applied."
          hint="Tier 1 applies a reply class, tags, a score or a next action at confidence 0.85 and above."
        />
      ) : (
        <ul className="space-y-1.5">
          {data.map((application) => (
            <Row key={application.id} application={application} showLead={!leadId} />
          ))}
        </ul>
      )}
    </Card>
  );
}
